import type { ReactNode } from 'react';
import { ArrowSquareOutIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { Num, Plural, T, Var, useGT } from 'gt-react';
import type { WorkbookOutput } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import type { WorkbookEditor } from './useWorkbookEditor';
import { WorkbookItemIcon } from './workbookItemIcon';
import { workbookItemIconKind } from './workbookItemIconKind';
import { useWorkbookProcess } from './useWorkbookProcess';
import {
  OutputValidationStatus,
  ValidatedDownloadButton,
} from './WorkbookOutputValidation';
import { outputValidationFailures } from './outputValidationFailures';
import {
  WorkbookOutputPreviewButton,
  WorkbookOutputStatus,
} from './WorkbookFileOutput';
import { downloadWorkbookOutput } from './workbookOutputDownload';
import {
  useWorkbookRunResults,
  type RunResultsEditor,
} from './workbookRunResults';
import {
  workbookOutputState,
  workbookOutputSummary,
  type WorkbookOutputSummary,
} from './workbookOutputs';
import { IconTooltip } from '@/components/common/IconTooltip';
import { HEAD_ACTIONS } from './WorkbookProcessContext';

/**
 * An Output's cell in the Process, after the Worksheets' cells: what it
 * produces from which Worksheets. It computes nothing, so no Instruction
 * nests under it; its Run menu runs up to the Worksheets it reads. An App
 * reads no Worksheet, so it shows neither a Run menu nor a Result.
 */
export function WorkbookOutputCell({
  output,
  runMenu,
  result,
}: {
  output: WorkbookOutput;
  runMenu?: ReactNode;
  result?: ReactNode;
}) {
  const gt = useGT();
  const { definition, openItem } = useWorkbookProcess();
  const readsSheets = output.kind !== 'app';
  return (
    <li
      id={`process-node-${output.id}`}
      className="min-w-0 scroll-mt-3 rounded-lg border border-input bg-card"
    >
      <div className="group/head flex items-center gap-2 rounded-t-lg border-b border-input bg-muted/70 px-3 py-2">
        <WorkbookItemIcon
          kind={workbookItemIconKind(definition, output.id) ?? 'xlsx'}
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">
          {output.name}
        </h3>
        <div className={HEAD_ACTIONS}>
          {readsSheets && runMenu}
          <IconTooltip>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={() => openItem(output.id)}
              aria-label={gt('Open {name}', {
                context: 'workbook open execution item',
                name: output.name,
              })}
            >
              <ArrowSquareOutIcon />
            </Button>
          </IconTooltip>
        </div>
      </div>
      <div className="flex flex-col gap-2 p-3">
        <WorkbookOutputDescription
          summary={workbookOutputSummary(definition, output)}
        />
        {readsSheets && result}
      </div>
    </li>
  );
}

/**
 * What an Output produces, in one line: a File's sheets and file name, or a
 * Report's charts and tables and the Worksheets they read, under its title
 * and description when it has them.
 */
function WorkbookOutputDescription({
  summary,
}: {
  summary: WorkbookOutputSummary;
}) {
  if (summary.kind === 'file')
    return <WorkbookFileOutputDescription summary={summary} />;
  if (summary.kind === 'sink')
    return <WorkbookSinkOutputDescription summary={summary} />;
  if (summary.kind === 'app')
    return <WorkbookAppOutputDescription summary={summary} />;
  return (
    <div className="flex flex-col gap-1 pl-6 text-sm">
      {summary.title && <p className="font-medium">{summary.title}</p>}
      {summary.description && (
        <p className="line-clamp-2 text-muted-foreground">
          {summary.description}
        </p>
      )}
      <p className="line-clamp-2 text-muted-foreground">
        <WorkbookReportContents summary={summary} />
      </p>
    </div>
  );
}

type FileSummary = Extract<WorkbookOutputSummary, { kind: 'file' }>;
type SinkSummary = Extract<WorkbookOutputSummary, { kind: 'sink' }>;
type ReportSummary = Extract<WorkbookOutputSummary, { kind: 'report' }>;
type AppSummary = Extract<WorkbookOutputSummary, { kind: 'app' }>;

function WorkbookAppOutputDescription({ summary }: { summary: AppSummary }) {
  return (
    <p className="line-clamp-2 pl-6 text-sm text-muted-foreground">
      {summary.appId ? (
        <T context="workbook output app description">
          App · built from this workbook
        </T>
      ) : (
        <T context="workbook output app description">
          App · Describe it to build it.
        </T>
      )}
    </p>
  );
}

function WorkbookSinkOutputDescription({ summary }: { summary: SinkSummary }) {
  const sheet = summary.sheetName;
  return (
    <p className="line-clamp-2 pl-6 text-sm text-muted-foreground">
      {sheet ? (
        <T context="workbook output sink description">
          Data sink · <Var>{sheet}</Var> · <Num>{summary.mappingCount}</Num>{' '}
          <Plural
            n={summary.mappingCount}
            one="column mapped"
            other="columns mapped"
          />
        </T>
      ) : (
        <T context="workbook output sink description">
          Data sink · Choose a worksheet to write.
        </T>
      )}
      {summary.deliverOnRun && (
        <T context="workbook output sink description">
          {' '}
          · writes after each run
        </T>
      )}
    </p>
  );
}

function WorkbookFileOutputDescription({ summary }: { summary: FileSummary }) {
  const names = summary.sheetNames.join(', ');
  return (
    <p className="line-clamp-2 pl-6 text-sm text-muted-foreground">
      {summary.sheetNames.length ? (
        <T context="workbook output file description">
          File · <Num>{summary.sheetNames.length}</Num>{' '}
          <Plural n={summary.sheetNames.length} one="sheet" other="sheets" />:{' '}
          <Var>{names}</Var> · <Var>{summary.fileName}</Var>
        </T>
      ) : (
        <T context="workbook output file description">
          File · <Var>{summary.fileName}</Var> · Add a sheet to it first.
        </T>
      )}
      {summary.csv && (
        <T context="workbook output file description"> · also as CSV</T>
      )}
    </p>
  );
}

/** A Report's charts and tables, and the Worksheets they read. */
function WorkbookReportContents({ summary }: { summary: ReportSummary }) {
  const names = summary.sheetNames.join(', ');
  const { charts, tables } = summary;
  if (!charts && !tables)
    return (
      <T context="workbook output report description">
        Report · No charts yet.
      </T>
    );
  if (!tables)
    return (
      <T context="workbook output report description">
        Report · <Num>{charts}</Num>{' '}
        <Plural n={charts} one="chart" other="charts" /> over <Var>{names}</Var>
      </T>
    );
  if (!charts)
    return (
      <T context="workbook output report description">
        Report · <Num>{tables}</Num>{' '}
        <Plural n={tables} one="table" other="tables" /> over <Var>{names}</Var>
      </T>
    );
  return (
    <T context="workbook output report description">
      Report · <Num>{charts}</Num>{' '}
      <Plural n={charts} one="chart" other="charts" /> and <Num>{tables}</Num>{' '}
      <Plural n={tables} one="table" other="tables" /> over <Var>{names}</Var>
    </T>
  );
}

/** Whether an Output's Worksheets are Current, and a File's download. */
export function WorkbookOutputResult({
  editor,
  output,
  busy,
  act,
}: {
  editor: Pick<WorkbookEditor, 'save' | 'workbookId'> & RunResultsEditor;
  output: WorkbookOutput;
  busy: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const results = useWorkbookRunResults(editor);
  const state = workbookOutputState(editor.definition, output, results);
  const failures = outputValidationFailures(editor.definition, output, results);
  return (
    <div className="flex flex-wrap items-center gap-2 pl-6">
      <WorkbookOutputStatus state={state} />
      {state === 'current' && <OutputValidationStatus failures={failures} />}
      {output.kind === 'file' && (
        <WorkbookOutputPreviewButton
          editor={editor}
          output={output}
          disabled={busy || state !== 'current'}
        />
      )}
      {output.kind === 'file' && (
        <ValidatedDownloadButton
          failures={failures}
          variant="outline"
          disabled={busy || state !== 'current'}
          onDownload={(accept) =>
            void act(() =>
              downloadWorkbookOutput(editor, output, 'xlsx', accept),
            )
          }
        >
          <DownloadSimpleIcon />
          <T context="workbook file output download">Download xlsx</T>
        </ValidatedDownloadButton>
      )}
    </div>
  );
}
